import React, { useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { Add, InfoOutlined } from "@mui/icons-material";
import { colors } from "../theme/designSystem";
import { DocumentTemplateVariable } from "../services/patientDocumentsService";

/**
 * Inserção de variáveis no texto (tipos de documento e modelos de contrato): busca com autocomplete
 * agrupada + botão "+" que insere no cursor do editor. Substitui a lista longa, que obrigava a rolar a
 * página para achar a variável. A mesma lista alimenta o atalho "{" dentro do editor (RichTextEditor).
 */
export interface VariableOption {
  /** Texto inserido, com chaves: {paciente.nome} */
  token: string;
  label: string;
  group: string;
  example?: string;
}

export const CUSTOM_FIELDS_GROUP = "Campos personalizados";

const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const toVariableOptions = (
  variables: DocumentTemplateVariable[],
  customFields: { key: string; label: string }[] = []
): VariableOption[] => [
  ...customFields.map((f) => ({ token: `{campo.${f.key}}`, label: f.label, group: CUSTOM_FIELDS_GROUP })),
  ...variables.map((v) => ({ token: `{${v.key}}`, label: v.label, group: v.group, example: v.example })),
];

/** Filtro por rótulo, chave e grupo, sem acento: "nasc", "cpf" e "responsavel" acham o que se espera. */
export const filterVariableOptions = (options: VariableOption[], query: string): VariableOption[] => {
  const term = normalize(query.trim());
  if (!term) return options;
  return options.filter((o) => normalize(`${o.label} ${o.token} ${o.group}`).includes(term));
};

export const renderVariableOption = (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: VariableOption) => {
  const { key, ...rest } = props;
  return (
    <li key={key ?? option.token} {...rest}>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.85rem", color: colors.textStrong }}>{option.label}</Typography>
        <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary, fontFamily: "monospace", wordBreak: "break-all" }}>
          {option.token}{option.example ? ` · ex.: ${option.example}` : ""}
        </Typography>
      </Box>
    </li>
  );
};

interface Example {
  title: string;
  /** Texto com variáveis; o resultado é montado com os exemplos do catálogo */
  template: string;
  multiline?: boolean;
}

interface Props {
  options: VariableOption[];
  onInsert: (token: string) => void;
  /** Explicação de quando a troca acontece (aparece no ícone de informação) */
  intro: string;
  examples?: Example[];
  disabled?: boolean;
  /** Distância do topo ao grudar: 85px na página (cabeçalho fixo), 0 dentro de modal */
  stickyTop?: number | string;
}

const VariablePicker: React.FC<Props> = ({ options, onInsert, intro, examples = [], disabled = false, stickyTop = 0 }) => {
  const [selected, setSelected] = useState<VariableOption | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);

  const fill = useMemo(() => {
    const byToken = new Map(options.filter((o) => o.example).map((o) => [o.token, o.example as string]));
    return (template: string) => template.replace(/\{[a-z_]+\.[a-z0-9_]+\}/gi, (t) => byToken.get(t) ?? t);
  }, [options]);

  const insert = (option: VariableOption | null) => {
    if (!option) return;
    onInsert(option.token);
    setSelected(null);
    setInputValue("");
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: stickyTop,
        zIndex: 3,
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1,
        py: 1,
        backgroundColor: colors.white,
      }}
    >
      <Autocomplete
        size="small"
        fullWidth
        disabled={disabled}
        options={options}
        groupBy={(o) => o.group}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.token === b.token}
        filterOptions={(opts, state) => filterVariableOptions(opts, state.inputValue)}
        value={selected}
        inputValue={inputValue}
        onInputChange={(_e, value, reason) => { if (reason !== "reset") setInputValue(value); }}
        onChange={(_e, value) => { setSelected(value); setInputValue(value?.label ?? ""); }}
        renderOption={renderVariableOption}
        noOptionsText="Nenhuma variável com esse nome"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Inserir variável no texto"
            placeholder='Busque por nome, ex.: "nascimento", "CPF", "data"'
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            onKeyDown={(e) => {
              // Com a lista fechada e uma variável escolhida, Enter insere (atalho do botão +)
              if (e.key === "Enter" && selected && !e.defaultPrevented) {
                e.preventDefault();
                insert(selected);
              }
            }}
          />
        )}
      />
      <Tooltip title={selected ? `Inserir ${selected.token} no cursor` : "Escolha uma variável para inserir"} arrow>
        <span>
          <IconButton
            onClick={() => insert(selected)}
            disabled={disabled || !selected}
            aria-label="Inserir variável no cursor"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "6px",
              backgroundColor: colors.primary,
              color: colors.white,
              "&:hover": { backgroundColor: colors.primaryHover },
              "&.Mui-disabled": { backgroundColor: colors.backgroundAlt, color: colors.textMuted },
            }}
          >
            <Add />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Como funcionam as variáveis" arrow>
        <IconButton onClick={(e) => setInfoAnchor(e.currentTarget)} aria-label="Como funcionam as variáveis" sx={{ color: colors.textSecondary }}>
          <InfoOutlined />
        </IconButton>
      </Tooltip>

      <Popover
        open={infoAnchor !== null}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { p: 2, maxWidth: 520, borderRadius: "8px" } }}
      >
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textStrong, mb: 1 }}>Variáveis de preenchimento automático</Typography>
        <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 1.25 }}>{intro}</Typography>
        <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 1.25 }}>
          Atalho: digite <strong>{"{"}</strong> no texto para buscar a variável ali mesmo, sem sair do editor.
        </Typography>
        {examples.map((ex) => (
          <Box key={ex.title} sx={{ border: "1px dashed #ced4da", borderRadius: "6px", p: 1, mb: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.25 }}>{ex.title}</Typography>
            <Typography sx={{ fontSize: "0.78rem", fontFamily: "monospace", whiteSpace: ex.multiline ? "pre-line" : "normal", wordBreak: "break-word", mb: 0.5 }}>{ex.template}</Typography>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.25 }}>Como sai na emissão:</Typography>
            <Typography sx={{ fontSize: "0.78rem", whiteSpace: ex.multiline ? "pre-line" : "normal", wordBreak: "break-word" }}>{fill(ex.template)}</Typography>
          </Box>
        ))}
      </Popover>
    </Box>
  );
};

export default VariablePicker;
